import { DefaultFilters } from "@/components/admin/fields/default-filters/default-filters";
import { SelectedFilters } from "@/lib/types/marketsuite/datatypes/filters";
import { PropertyListingType } from "../types/marketsuite/datatypes/property";

/**
 * Resolves dynamic default filter placeholders against route variables.
 *
 * @param filters - Default filter values from Payload.
 * @param valueMap - Route variables available for placeholder replacement.
 * @returns Default filters with dynamic placeholders resolved when possible.
 */
export function resolveDynamicFilters(filters?: DefaultFilters["filters"], valueMap?: Record<string, string> | undefined): Record<string, string> | undefined {
	if (!filters || typeof filters !== "object" || Array.isArray(filters)) return undefined;

	const keys = Object.keys(filters);
	const resolvedFilters: Record<string, string> = {};

	for (let k = 0; k < keys.length; k++) {
		const key = keys[k]!;
		const filterValue = (filters as Record<string, unknown>)[key];

		if (typeof filterValue === "string") {
			resolvedFilters[key] = filterValue
				.split(",")
				.map((value) => value.trim())
				.map((value) => (value.startsWith(":") ? valueMap?.[value.replaceAll(":", "")] : value))
				.filter((value) => typeof value === "string" && value.length > 0)
				.join(",");
		}
	}

	return resolvedFilters;
}

/**
 * Builds a query string from selected filters while omitting defaults.
 *
 * @param selectedFilters - Active filter values selected by the user.
 * @param defaultFilters - Default filter values that should be omitted from the query.
 * @param hiddenFilters - Filter keys that should never appear in the query.
 * @param propertyType - Current property type to omit from property-type filters.
 * @param page - Current results page.
 * @param sort - Current sort order; an empty string explicitly clears the default sort.
 * @returns URL query string for non-default selected filters, sorting and pagination.
 */
export function buildFilterQuery({
	selectedFilters,
	defaultFilters,
	hiddenFilters,
	listingType,
	page = 0,
	sort,
}: {
	selectedFilters: SelectedFilters;
	defaultFilters?: Record<string, string>;
	hiddenFilters?: string[];
	listingType: PropertyListingType | undefined;
	page?: number;
	sort?: string;
}): string {
	const params = new URLSearchParams();
	if (page > 1) params.set("page", page.toString());
	if (sort !== undefined) params.set("sort", sort);

	for (const [key, value] of Object.entries(selectedFilters)) {
		if (value == null) continue;
		if (hiddenFilters?.includes(key)) continue;
		const defaultValues = defaultFilters?.[key]?.split(",").filter(Boolean) ?? [];

		if (Array.isArray(value)) {
			if (typeof value[0] === "number") {
				const serialized = value.join(",");

				if (serialized !== defaultValues.map((entry) => parseFloat(entry)).join(",")) {
					params.set(key, serialized);
				}

				continue;
			}

			for (const item of value) {
				if (defaultValues.includes(item.toString())) continue;
				if (listingType && key === "listing-type" && item === listingType) continue;

				params.append(key, item.toString());
			}
		} else if (typeof value === "number") {
			const serialized = value.toString();

			if (!defaultValues.includes(serialized)) {
				params.set(key, serialized);
			}
		} else if (!defaultValues.includes(value.toString())) {
			params.set(key, value.toString());
		}
	}

	return params.toString();
}
